/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useState } from "react";
import { SpinnerLoading } from "../utils/SpinnerLoading";
import { ErrorMessage } from "../utils/ErrorMessage";
import { fetchRecords, formatCurrency, htmlCodeAssist } from "../utils/AppUtil";

class Donation {
    date: string;
    donor: string;
    deceased: string;
    isLeaf: boolean;
    currency: string;
    amount: number;
    constructor(date: string, donor: string, deceased: string, isLeaf: boolean, currency: string, amount: number) {
        this.date = date;
        this.donor = donor;
        this.deceased = deceased;
        this.isLeaf = isLeaf;
        this.currency = currency;
        this.amount = amount;
    }
}

type RecentDonorProps = {
    endpoint: string; 
    recordCount: number;
}

export const RecentDonorsDefault: React.FC<RecentDonorProps> = (props): JSX.Element => {

    const [records, setRecords] = useState<Donation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [httpError, setHttpError] = useState(null);
    const [endpoint, setEndpoint] = useState(`${props.endpoint}?count=${props.recordCount}`);

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {

        fetchRecords(endpoint, processData)
        .catch((error) => {
            setIsLoading(false);
            setHttpError(error.message);
        });

    }, [endpoint]);

    // You may edit this function to manipulate the way your code is processed.
    const processData = (responseJson: any) => { // Do not edit these parameters, just the content of this function.
        const recordData: Donation[] = [];
    
        for (const key in responseJson) {
            const record: any = responseJson[key];
            const displayDate: string = record["transactionDateDisplay"];
    
            let deceased: string = "";
            let donor: string = "Anonymous";
            let isAnonymous: boolean = record["isAnonymous"]

            const isLeaf: boolean = record["isLeaf"];
            const currency = record["currencyDisplaySymbol"];
            const amount:number = record["amount"];

            if (isLeaf) {

                if (!isAnonymous) {
                    donor = (record["subscriberFirstName"] ? record["subscriberFirstName"] : "") + " " + (record["subscriberLastName"] ? record["subscriberLastName"] : "");
                }
        
                deceased = (record["deceasedFirstName"] ? record["deceasedFirstName"] : " ") + " " + (record["deceasedLastName"] ? record["deceasedLastName"] : "");
                if (!record["deceasedId"]) {
                    deceased = "Not Specified";
                }

            } else {
                donor = (record["donorName"]);

                deceased = (record["donorMessage"]);
                if (!deceased) {
                    deceased = "Not Specified";
                }
            }



            if(amount > 0) {
                recordData.push(new Donation(displayDate, donor, deceased, isLeaf, currency, amount));
            }
        }
        setRecords(recordData);
        setIsLoading(false);
    }

    if(isLoading) {
        return(
            <SpinnerLoading />
        )
    }

    if(httpError) {
        return(
            <ErrorMessage error={httpError} />
        )
    }

    return (
        <div className="card card-recent-funerals jc-border-12">
            <div className="card-header card-header-bg">
                <p className="jc-txt-heading fs-5">Thank You To Our Most Recent Donors</p>
            </div>

            <div className="card-body p-0">
                <div className="jc-notification-wrapper p-3">
                    <div className="jc-notification d-flex align-items-center gap-2 jc-txt-body fs-6 px-3 py-2">
                        <img src="/images/icon-info.png" />
                        Donations can be made anonymously
                    </div>
                </div>
                <div className="table-donors">
                    <table className="table table-striped mb-0">
                        <thead>
                            <tr>
                                <th className="jc-txt-body fs-5 fw-bold" scope="">Date</th>
                                <th className="jc-txt-body fs-5 fw-bold" scope="">Donor</th>
                                <th className="jc-txt-body fs-5 fw-bold" scope="">In Memory of</th>
                                <th className="jc-txt-body fs-5 fw-bold align-end" scope="">Donation</th>
                            </tr>
                        </thead>
                        <tbody>
                            <RecentDonorsRecords records={records} />
                        </tbody>
                    </table>
                </div>
                
                <div className="card-footer d-flex justify-content-end">
                    <a href="/donate" className="btn btn-dark jc-txt-body jc-txt-light">Donate</a>
                </div>

            </div>
        </div>
    );
}

const RecentDonorsRecords: React.FC<{records: Donation[];}>= ({records}): JSX.Element => {

    const result: React.ReactElement[] = [];

    records.forEach((donation, index) => {
        result.push(
            <tr key={index}>
                <td className="jc-txt-body fs-6" style={{padding: "11px 16px"}}>{donation.date}</td>
                <td className="jc-txt-body fs-6" style={{padding: "11px 16px"}}>{donation.donor}</td>
                <td className="jc-txt-body fs-6" style={{padding: "11px 16px"}}>{donation.deceased}</td>
                <td className="jc-txt-body fs-3 d-flex justify-content-end gap-3" style={{padding: "11px 16px"}}>
                    {donation.isLeaf && 
                        <svg width="17" height="35" viewBox="0 0 17 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.565762 21.3925C2.82877 30.0647 7.47012 33.3276 11.7654 31.8946C16.0553 30.4617 18.3842 24.8788 16.1157 16.2066C13.8527 7.54069 3.5593 0.5 3.5593 0.5C3.5593 0.5 -1.69724 12.7266 0.565762 21.3925Z" fill="#A6E9D5"/>
                            <path d="M0.565763 21.3925C2.82877 30.0647 7.47013 33.3276 11.7654 31.8946L3.5593 0.5C3.5593 0.5 -1.69724 12.7266 0.565763 21.3925Z" fill="#4DD4AC"/>
                            <path d="M7.7831 7.57812L6.46484 11.6102L6.83286 13.0184L7.7831 7.57812Z" fill="#4DD4AC"/>
                            <path d="M5.93121 4.24609L5.15674 6.61573L5.37095 7.44077L5.93121 4.24609Z" fill="#4DD4AC"/>
                            <path d="M10.1211 11.1484L8.02283 17.5874L8.61055 19.833L10.1211 11.1484Z" fill="#4DD4AC"/>
                            <path d="M12.8406 16.0508L9.94043 24.9214L10.7534 28.023L12.8406 16.0508Z" fill="#4DD4AC"/>
                            <path d="M3.25623 7.61719L6.03555 9.96822L6.3706 11.2523L3.25623 7.61719Z" fill="#A6E9D5"/>
                            <path d="M3.21228 4.03516L4.84362 5.41228L5.04136 6.16907L3.21228 4.03516Z" fill="#A6E9D5"/>
                            <path d="M3.03271 11.6602L7.45985 15.4069L7.99265 17.454L3.03271 11.6602Z" fill="#A6E9D5"/>
                            <path d="M3.10266 16.9258L9.20509 22.0931L9.94111 24.9156L3.10266 16.9258Z" fill="#A6E9D5"/>
                            <path d="M11.7153 34.4978L13.0665 34.045L11.8196 30.2734L10.9463 30.565L11.7153 34.4978Z" fill="#4DD4AC"/>
                            <path d="M11.3811 30.4223L12.3918 34.2745L13.0674 34.045L11.8205 30.2734L11.3811 30.4223Z" fill="#A6E9D5"/>
                        </svg>
                    }
                    {htmlCodeAssist(donation.currency)}
                    {formatCurrency(donation.amount)}
                </td>
            </tr>
        )
    });

    return(
        <>{result}</>
    );
}
